import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ChevronLeft, Send, Sparkles, Trash2, Volume2, Square, Plus, FileText,
  ChevronDown, ChevronUp, MessageCircle, Loader2,
} from "lucide-react";

function Bubble({ m, t, onSpeak, loadingTts, playing }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`eva-msg-${m.role}`}>
      <div className={`max-w-[88%] rounded-2xl border p-3 ${isUser ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground"}`}>
        {!isUser && (
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary"><Sparkles className="h-3 w-3" /> {t("weva")}</span>
            <button onClick={() => onSpeak(m)} disabled={loadingTts} data-testid={`eva-tts-${m.id}`}
              className="rounded-lg p-1 text-primary transition-colors hover:bg-accent" title={playing ? t("weva_tts_stop") : t("weva_tts_play")}>
              {loadingTts ? <Loader2 className="h-4 w-4 animate-spin" /> : playing ? <Square className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
          </div>
        )}
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{m.text}</p>
      </div>
    </div>
  );
}

function ChatView({ t, lang }) {
  const [messages, setMessages] = useState(null);
  const [limits, setLimits] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [ttsId, setTtsId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    const [hist, lim] = await Promise.all([
      api.get("/eva/chat/history").then((r) => r.data).catch(() => []),
      api.get("/eva/chat/limits").then((r) => r.data).catch(() => null),
    ]);
    setMessages(Array.isArray(hist) ? hist : []);
    setLimits(lim);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (messages) setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 80);
  }, [messages]);

  const stopPlayback = () => {
    try { audioRef.current?.pause(); } catch { /* ignore */ }
    audioRef.current = null;
    setPlayingId(null);
  };
  useEffect(() => () => stopPlayback(), []);

  const speak = async (m) => {
    if (playingId === m.id) { stopPlayback(); return; }
    if (ttsId) return;
    stopPlayback();
    setTtsId(m.id);
    try {
      const { data } = await api.post("/eva/tts", { text: m.text, voice: "nova" });
      if (!data?.audio_base64) throw new Error("empty audio");
      const audio = new Audio(`data:audio/${data.format || "mp3"};base64,${data.audio_base64}`);
      audioRef.current = audio;
      audio.onended = () => stopPlayback();
      setPlayingId(m.id);
      await audio.play();
    } catch (e) {
      toast.error(apiError(e, "TTS failed"));
      stopPlayback();
    } finally { setTtsId(null); }
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setSending(true);
    const tmp = { id: `tmp-${Date.now()}`, role: "user", text: msg, created_at: new Date().toISOString() };
    setMessages((m) => [...(m || []), tmp]);
    setInput("");
    try {
      const { data } = await api.post("/eva/chat", { message: msg, lang });
      setMessages((m) => [...m.filter((x) => x.id !== tmp.id), data.user_message, data.assistant_message]);
      setLimits((prev) => (prev ? { ...prev, used_today: data.used_today } : prev));
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== tmp.id));
      const em = String(e?.response?.status === 402 ? t("eva_chat_limit_hit") : apiError(e));
      toast.error(em);
    } finally { setSending(false); }
  };

  const clearAll = async () => {
    if (!window.confirm(t("eva_chat_clear_confirm"))) return;
    try { await api.delete("/eva/chat"); setMessages([]); } catch (e) { toast.error(apiError(e)); }
  };

  if (messages === null) return <Spinner label={t("common.loading")} />;

  const usedLine = limits
    ? t("eva_chat_used", { n: limits.used_today ?? 0, q: (limits.daily_messages ?? 0) >= 9999 ? "∞" : (limits.daily_messages ?? 0) })
    : "";

  return (
    <div className="flex h-[calc(100vh-13rem)] flex-col">
      {usedLine ? <p className="mb-2 text-xs font-medium text-primary" data-testid="eva-chat-used">{usedLine}</p> : null}
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto rounded-2xl border border-border bg-secondary/20 p-4">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <Sparkles className="h-7 w-7 text-primary" />
            <p className="font-display text-base text-foreground">{t("eva_chat_empty")}</p>
            <p className="max-w-sm text-sm text-muted-foreground">{t("eva_chat_send_hint")}</p>
          </div>
        ) : (
          messages.map((m) => (
            <Bubble key={m.id} m={m} t={t} onSpeak={speak} loadingTts={ttsId === m.id} playing={playingId === m.id} />
          ))
        )}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-2xl border border-border bg-card p-3">
              <span className="inline-flex items-center gap-2 text-sm italic text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("eva_chat_thinking")}</span>
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-end gap-2">
        <Textarea
          data-testid="eva-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={t("eva_chat_placeholder")}
          maxLength={2000}
          className="max-h-32 min-h-[46px] flex-1 resize-none rounded-2xl"
        />
        <Button onClick={send} disabled={!input.trim() || sending} data-testid="eva-chat-send" className="h-[46px] w-[46px] shrink-0 rounded-full p-0">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
      {messages.length > 0 && (
        <button onClick={clearAll} data-testid="eva-chat-clear" className="mt-2 inline-flex items-center justify-center gap-1.5 self-center text-xs text-muted-foreground hover:text-foreground">
          <Trash2 className="h-3.5 w-3.5" /> {t("eva_chat_clear")}
        </button>
      )}
    </div>
  );
}

function PastView({ t, lang }) {
  const [sessions, setSessions] = useState(null);
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/eva/chat/sessions"); setSessions(Array.isArray(data) ? data : []); }
    catch { setSessions([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const newSession = async () => {
    try { await api.post("/eva/chat/new"); toast.success(t("eva_chat_new_session")); } catch (e) { toast.error(apiError(e)); }
  };

  const makeReport = async (sid) => {
    if (busy) return;
    setBusy(sid);
    try {
      await api.post(`/eva/chat/sessions/${sid}/report`, null, { params: { language: lang } });
      toast.success(t("weva_saved") || "OK");
      navigate("/app/weva/chat?tab=reports");
      window.location.hash = "reports";
    } catch (e) {
      if (e?.response?.status === 402) {
        if (window.confirm(t("weva_credits_needed"))) navigate("/app/credits");
      } else { toast.error(apiError(e, t("popup_report_failed"))); }
    } finally { setBusy(null); }
  };

  if (sessions === null) return <Spinner label={t("common.loading")} />;

  return (
    <div className="space-y-3">
      <Button onClick={newSession} data-testid="eva-chat-new-session" className="w-full gap-1.5 rounded-full">
        <Plus className="h-4 w-4" /> {t("eva_chat_new_session")}
      </Button>
      {sessions.length === 0 ? (
        <EmptyState icon={MessageCircle} title={t("eva_chat_past_empty")} />
      ) : sessions.map((sess) => (
        <div key={sess.session_id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              {sess.is_current ? <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase text-primary-foreground">{t("eva_chat_current_pill")}</span> : null}
              <span className="text-xs text-muted-foreground">{sess.last_at ? new Date(sess.last_at).toLocaleString() : "—"}</span>
              <span className="text-xs text-muted-foreground">· {sess.message_count} {t("eva_chat_msgs_short")}</span>
            </div>
            <p className="line-clamp-2 text-sm text-foreground">{sess.first_message || t("eva_chat_empty_session")}</p>
          </div>
          <button onClick={() => makeReport(sess.session_id)} disabled={!!busy} data-testid={`eva-session-report-${sess.session_id}`}
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-primary px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-60">
            {busy === sess.session_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
            {busy === sess.session_id ? t("eva_chat_reflecting") : sess.report_id ? t("eva_chat_view_report") : t("eva_chat_make_report")}
          </button>
        </div>
      ))}
    </div>
  );
}

function ReportsView({ t }) {
  const [reports, setReports] = useState(null);
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    try { const { data } = await api.get("/eva/chat/reports"); setReports(Array.isArray(data) ? data : []); }
    catch { setReports([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try { await api.delete(`/eva/chat/reports/${id}`); setReports((r) => r.filter((x) => x.id !== id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (reports === null) return <Spinner label={t("common.loading")} />;
  if (reports.length === 0) return <EmptyState icon={FileText} title={t("eva_chat_reports_empty")} />;

  return (
    <div className="space-y-3">
      {reports.map((r) => {
        const isOpen = !!open[r.id];
        return (
          <div key={r.id} className="overflow-hidden rounded-2xl border border-border bg-card">
            <button onClick={() => setOpen((p) => ({ ...p, [r.id]: !p[r.id] }))} data-testid={`eva-report-${r.id}`} className="flex w-full items-center gap-3 p-4 text-left">
              <Sparkles className="h-4 w-4 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-sm text-foreground">{r.summary || t("eva_chat_report_fallback")}</p>
                <p className="mt-1 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()} · {r.message_count} {t("eva_chat_msgs_short")}</p>
              </div>
              {isOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>
            {isOpen && (
              <div className="space-y-2 border-t border-border p-4 pt-3">
                {(r.insights || []).length > 0 && (
                  <>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("insights_2")}</p>
                    {(r.insights || []).map((it, i) => (
                      <div key={i} className="flex gap-2 text-sm text-foreground"><span className="text-primary">•</span><span>{it}</span></div>
                    ))}
                  </>
                )}
                {(r.themes || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {r.themes.map((th, i) => <span key={i} className="rounded-full bg-primary/15 px-2.5 py-0.5 text-xs text-primary">{th}</span>)}
                  </div>
                )}
                <button onClick={() => remove(r.id)} data-testid={`eva-report-delete-${r.id}`} className="mt-2 inline-flex items-center gap-1 text-xs text-rose-600 hover:underline">
                  <Trash2 className="h-3.5 w-3.5" /> {t("delete_report")}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function EvaChat() {
  const { t, lang } = useI18n();
  usePageMeta(t("eva_chat"));
  const navigate = useNavigate();
  const initialTab = typeof window !== "undefined" && window.location.hash === "#reports" ? "reports" : "chat";
  const [tab, setTab] = useState(initialTab);

  return (
    <div className="space-y-4" data-testid="eva-chat-screen">
      <button onClick={() => navigate("/app/weva")} data-testid="eva-chat-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> {t("weva")}
      </button>
      <div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground"><Sparkles className="h-3 w-3" /> {t("weva")}</span>
          <h1 className="font-display text-2xl text-foreground">{t("eva_chat")}</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{t("eva_chat_sub")}</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="chat" data-testid="eva-chat-tab-chat">{t("eva_chat_tab_chat")}</TabsTrigger>
          <TabsTrigger value="past" data-testid="eva-chat-tab-past">{t("eva_chat_tab_past")}</TabsTrigger>
          <TabsTrigger value="reports" data-testid="eva-chat-tab-reports">{t("eva_chat_tab_reports")}</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "chat" ? <ChatView t={t} lang={lang} /> : tab === "past" ? <PastView t={t} lang={lang} /> : <ReportsView t={t} />}
    </div>
  );
}
